import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Grid,
} from '@mui/material';
import { 
  FileDownloadOutlined as DownloadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { baseUrls } from '../../../constants/module-constants';
import { useAuthContext } from '../../../context/auth-context';
import AffiliationFilter from '../../../components/reports/AffiliationFilter';
import { getAffiliationZones } from '../../../utils/report-filters';
import { authFetch } from '../../../lib/http';

const NotasGeneralesPage = () => {
  const { activeCamp } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [grades, setGrades] = useState([]);
  const [orderBy, setOrderBy] = useState('');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [filters, setFilters] = useState({
    eventName: '',
    affiliationZone: '',
    clubName: '',
    grade: '',
  });

  useEffect(() => {
    if (!activeCamp?._id) {
      setGrades([]);
      return;
    }
    fetchGrades(activeCamp._id);
  }, [activeCamp?._id]);

  const fetchGrades = async (campId) => {
    setLoading(true);
    try {
      const response = await authFetch(`${baseUrls.baseUrl}/grade/?campId=${campId}`);
      const data = await response.json();
      setGrades(data.results);
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleFilterChange = (column, value) => {
    setFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const getUniqueValues = (field) => {
    // Hay filas sin nota (grade null/undefined): se descartan para no romper el Select
    const uniqueValues = [...new Set(grades.map(grade => grade[field]))]
      .filter(v => v !== null && v !== undefined && v !== '');
    return uniqueValues.sort();
  };

  const getFilteredAndSortedGrades = () => {
    let filteredGrades = grades.filter((grade) => {
      return (
        (!filters.eventName || grade.eventName === filters.eventName) &&
        (!filters.affiliationZone || grade.affiliationZone === filters.affiliationZone) &&
        (!filters.clubName || grade.clubName === filters.clubName) &&
        (!filters.grade || String(grade.grade ?? '') === filters.grade)
      );
    });

    if (orderBy) {
      filteredGrades.sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return orderDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        
        if (orderDirection === 'asc') {
          return aStr.localeCompare(bStr);
        }
        return bStr.localeCompare(aStr);
      });
    }

    return filteredGrades;
  };

  const exportToCsv = () => {
    const headers = ['Event', 'Asociación/Misión', 'Zona', 'Club', 'Grade'];
    const csvRows = [headers.join(',')];

    const filteredGrades = getFilteredAndSortedGrades();
    filteredGrades.forEach((grade) => {
      const row = [
        grade.eventName,
        grade.affiliationName,
        grade.affiliationZone,
        grade.clubName,
        grade.grade,
      ].map((cell) => `"${cell}"`);
      csvRows.push(row.join(','));
    });

    const csvString = '\uFEFF' + csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'notas_generales.csv';
    link.click();
  };

  const filteredAndSortedGrades = getFilteredAndSortedGrades();
  const zones = getAffiliationZones(grades);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Puntuación General</Typography>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={exportToCsv}
        >
          Exportar CSV
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <AffiliationFilter
            value={filters.affiliationZone}
            onChange={(v) => handleFilterChange('affiliationZone', v)}
            zones={zones}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Typography variant="body2" color="text.secondary">
            {filteredAndSortedGrades.length} de {grades.length} registros
            {filters.affiliationZone ? ` · ${filters.affiliationZone}` : ''}
          </Typography>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <strong>Evento</strong>
                    <IconButton size="small" onClick={() => handleSort('eventName')}>
                      {orderBy === 'eventName' && orderDirection === 'desc' ? 
                        <ArrowDownwardIcon fontSize="small" /> : 
                        <ArrowUpwardIcon fontSize="small" />
                      }
                    </IconButton>
                  </Box>
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <Select
                      value={filters.eventName}
                      onChange={(e) => handleFilterChange('eventName', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {getUniqueValues('eventName').map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <strong>Asociación/Misión</strong>
                    <IconButton size="small" onClick={() => handleSort('affiliationName')}>
                      {orderBy === 'affiliationName' && orderDirection === 'desc' ? 
                        <ArrowDownwardIcon fontSize="small" /> : 
                        <ArrowUpwardIcon fontSize="small" />
                      }
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <strong>Zona</strong>
                    <IconButton size="small" onClick={() => handleSort('affiliationZone')}>
                      {orderBy === 'affiliationZone' && orderDirection === 'desc' ? 
                        <ArrowDownwardIcon fontSize="small" /> : 
                        <ArrowUpwardIcon fontSize="small" />
                      }
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <strong>Club</strong>
                    <IconButton size="small" onClick={() => handleSort('clubName')}>
                      {orderBy === 'clubName' && orderDirection === 'desc' ? 
                        <ArrowDownwardIcon fontSize="small" /> : 
                        <ArrowUpwardIcon fontSize="small" />
                      }
                    </IconButton>
                  </Box>
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <Select
                      value={filters.clubName}
                      onChange={(e) => handleFilterChange('clubName', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {getUniqueValues('clubName').map((value) => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <strong>Calificación</strong>
                    <IconButton size="small" onClick={() => handleSort('grade')}>
                      {orderBy === 'grade' && orderDirection === 'desc' ? 
                        <ArrowDownwardIcon fontSize="small" /> : 
                        <ArrowUpwardIcon fontSize="small" />
                      }
                    </IconButton>
                  </Box>
                  <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                    <Select
                      value={filters.grade}
                      onChange={(e) => handleFilterChange('grade', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {getUniqueValues('grade').map((value) => (
                        <MenuItem key={value} value={value.toString()}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedGrades.map((grade, index) => (
                <TableRow key={index}>
                  <TableCell>{grade.eventName}</TableCell>
                  <TableCell>{grade.affiliationName}</TableCell>
                  <TableCell>{grade.affiliationZone}</TableCell>
                  <TableCell>{grade.clubName}</TableCell>
                  <TableCell>{grade.grade}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default NotasGeneralesPage;
